import { getApiBaseUrl } from "../config/apiBase";
import { apiFetch } from "../config/apiFetch";
import type { StandardResource, StandardResourceItem } from "../types";

async function parseError(res: Response): Promise<string> {
  const text = await res.text();
  try {
    const j = JSON.parse(text) as { error?: string };
    if (j.error) return j.error;
  } catch {
    /* ignore */
  }
  return text || `Request failed (${res.status})`;
}

export async function fetchStandardResources(): Promise<StandardResource[]> {
  const res = await apiFetch(`${getApiBaseUrl()}/api/standard-resources`);
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<StandardResource[]>;
}

export async function createStandardResourceApi(input: {
  title: string;
  notes?: string;
}): Promise<StandardResource> {
  const res = await apiFetch(`${getApiBaseUrl()}/api/standard-resources`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ title: input.title, notes: input.notes ?? "" }),
  });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<StandardResource>;
}

export async function updateStandardResourceApi(
  id: string,
  patch: Partial<{ title: string; notes: string; sortOrder: number }>,
): Promise<StandardResource> {
  const res = await apiFetch(`${getApiBaseUrl()}/api/standard-resources/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<StandardResource>;
}

export async function deleteStandardResourceApi(id: string): Promise<void> {
  const res = await apiFetch(`${getApiBaseUrl()}/api/standard-resources/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
  if (!res.ok && res.status !== 204) throw new Error(await parseError(res));
}

export async function reorderStandardResources(orderedIds: string[]): Promise<StandardResource[]> {
  const res = await apiFetch(`${getApiBaseUrl()}/api/standard-resources/reorder`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ orderedIds }),
  });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<StandardResource[]>;
}

export async function createStandardResourceItemApi(
  resourceId: string,
  item: {
    label: string;
    quantity: number;
    unit: string;
    rate?: number;
    hsnCode?: string;
    catalogStandardItemId?: string | null;
  },
): Promise<StandardResourceItem> {
  const res = await apiFetch(
    `${getApiBaseUrl()}/api/standard-resources/${encodeURIComponent(resourceId)}/items`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(item),
    },
  );
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<StandardResourceItem>;
}

export async function updateStandardResourceItemApi(
  resourceId: string,
  itemId: string,
  patch: Partial<
    Pick<StandardResourceItem, "label" | "quantity" | "unit" | "rate" | "hsnCode" | "catalogStandardItemId"> & {
      sortOrder?: number;
    }
  >,
): Promise<StandardResourceItem> {
  const res = await apiFetch(
    `${getApiBaseUrl()}/api/standard-resources/${encodeURIComponent(resourceId)}/items/${encodeURIComponent(itemId)}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    },
  );
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<StandardResourceItem>;
}

export async function deleteStandardResourceItemApi(resourceId: string, itemId: string): Promise<void> {
  const res = await apiFetch(
    `${getApiBaseUrl()}/api/standard-resources/${encodeURIComponent(resourceId)}/items/${encodeURIComponent(itemId)}`,
    { method: "DELETE" },
  );
  if (!res.ok && res.status !== 204) throw new Error(await parseError(res));
}
